import cx from 'classnames';
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Icon } from '@sb/design-system';

import { AssistantMessage } from './AssistantMessage';
import { ConfirmationCard } from './ConfirmationCard';
import { EquipmentCard } from './EquipmentCard';
import { NavigationCard } from './NavigationCard';
import { OptionsPickerCard } from './OptionsPickerCard';
import { DEFAULT_STEP_DELAY_MS, ServerToolGroup } from './ServerToolGroup';
import { ThinkingIndicator } from './ThinkingIndicator';
import type { ChatCard, ChatMessage, ServerToolGroupCard } from './types';
import { UserMessage } from './UserMessage';

interface MessageListProps {
  messages: ChatMessage[];
  /**
   * Fires when the user taps the action button on an embedded card.
   * The argument is the full label string — the chat mount injects it
   * into the input and auto-sends so the pick lands in the
   * conversation as if the user had typed it.
   */
  onCardConfirm?: (label: string) => void;
  /**
   * Fires when the user taps a `navigation` card. The argument is the
   * card's in-app `url`; the chat mount wires this to react-router's
   * navigate so the page swaps behind the still-open chat panel.
   */
  onCardNavigate?: (url: string) => void;
  /**
   * Optional renderer that returns a feedback slot for each
   * non-streaming assistant message (typically a `MessageFeedback`
   * thumbs row). Caller-supplied so this list stays decoupled from
   * the feedback persistence wiring.
   */
  renderFeedback?: (message: ChatMessage, index: number) => ReactNode;
  /**
   * Fires when the user taps the "Not sent. Tap to retry." affordance
   * under a user message whose `sendStatus` is 'failed'. The mount
   * wires this to a retry helper that re-runs `transport.send` with
   * the message's text.
   */
  onRetry?: (messageID: string) => void;
  /**
   * Render a ThinkingIndicator (pulsing dots) below the newest message
   * while a turn is in flight but nothing else is animating. Driven by
   * `useChatMessages.isThinking`.
   */
  isThinking?: boolean;
  className?: string;
}

// How close to the bottom (px) still counts as "following the conversation".
// Within this band we keep auto-scrolling as new content streams in; scroll
// further up than this and we treat it as the user reading history and stop
// yanking them back down.
const NEAR_BOTTOM_PX = 80;

function noop() {}

interface ChatCardSlotProps {
  card: ChatCard;
  onConfirm: ((label: string) => void) | undefined;
  onNavigate: ((url: string) => void) | undefined;
}

/**
 * Merge a run of consecutive `toolGroup` messages into one card so the
 * transcript shows a single "{x} tools ran" ticker for the whole run.
 * Tool batches only split when something else lands between them —
 * assistant text, a user message, another card kind. Runs can span
 * message boundaries (persist events and history seeding fan one
 * logical batch into several bubbles).
 *
 * `delays` only matter to the uncontrolled mock animation; they're
 * padded with the default per-step delay so per-tool alignment
 * survives a merge where some cards specified delays and others
 * didn't.
 */
function mergeToolGroupRun(run: ServerToolGroupCard[]): ServerToolGroupCard {
  return {
    kind: 'toolGroup',
    tools: run.flatMap((card) => card.tools),
    delays: run.some((card) => card.delays)
      ? run.flatMap(
          (card) => card.delays ?? card.tools.map(() => DEFAULT_STEP_DELAY_MS),
        )
      : undefined,
    controlled: run.some((card) => card.controlled),
  };
}

/**
 * Inline dispatch on `cardKind`. Six live card kinds:
 * `equipment_confirm`, `confirmation`, `alertConfirmation`,
 * `toolGroup`, `optionsPicker`, and `navigation`. The unknown-kind
 * branch keeps the stream rendering by warning + skipping rather than
 * crashing the message list. Future kinds plug into the same switch.
 */
function ChatCardSlot({ card, onConfirm, onNavigate }: ChatCardSlotProps) {
  const handleConfirm = onConfirm ?? noop;
  const handleNavigate = onNavigate ?? noop;

  switch (card.kind) {
    case 'equipment_confirm':
      return (
        <EquipmentCard
          gripperName={card.gripperName}
          onConfirm={handleConfirm}
        />
      );
    case 'confirmation':
      return (
        <ConfirmationCard
          kind="confirmation"
          label={card.label}
          onConfirm={handleConfirm}
        />
      );
    case 'alertConfirmation':
      return (
        <ConfirmationCard
          kind="alertConfirmation"
          label={card.label}
          alertTitle={card.alertTitle}
          alertBody={card.alertBody}
          onConfirm={handleConfirm}
        />
      );
    case 'toolGroup':
      return (
        <ServerToolGroup
          tools={card.tools}
          delays={card.delays}
          controlled={card.controlled}
        />
      );
    case 'optionsPicker':
      return (
        <OptionsPickerCard options={card.options} onConfirm={handleConfirm} />
      );
    case 'navigation':
      return (
        <NavigationCard
          label={card.label}
          url={card.url}
          onNavigate={handleNavigate}
        />
      );
    default: {
      // eslint-disable-next-line no-console
      console.warn('MessageList: unknown chat card kind', card);

      return null;
    }
  }
}

/**
 * One entry in the transcript's render plan: either a plain message, or
 * a run of consecutive `toolGroup` messages already merged into a
 * single card (keyed on the run's first message id so the component
 * instance — and its expand state — survives tools being appended).
 */
type TranscriptItem =
  | { kind: 'message'; message: ChatMessage }
  | { kind: 'mergedToolGroup'; id: string; card: ServerToolGroupCard };

/**
 * Scrollable column of chat messages. Newest messages appear at the
 * bottom; the container flexes to fill the available space and scrolls
 * inside the chat panel. Messages carrying an embedded `card` are
 * routed to the matching card renderer; cards with unknown kinds log
 * a warning and render nothing.
 */
export function MessageList({
  messages,
  onCardConfirm,
  onCardNavigate,
  renderFeedback,
  onRetry,
  isThinking = false,
  className,
}: MessageListProps) {
  const isAnyAssistantStreaming = messages.some(
    (message) => message.role === 'assistant' && message.isStreaming,
  );

  // Group consecutive toolGroup messages and merge each run ONCE per
  // messages change. Memoizing keeps the merged card (and its freshly
  // flatMapped `tools` array) referentially stable across unrelated
  // re-renders — scroll, isThinking — so ServerToolGroup's controlled
  // resync effect only fires when message data actually changes.
  const transcriptItems = useMemo(() => {
    const items: TranscriptItem[] = [];

    for (let index = 0; index < messages.length; index += 1) {
      const message = messages[index];

      if (message.card?.kind === 'toolGroup') {
        const run: ServerToolGroupCard[] = [message.card];

        while (index + 1 < messages.length) {
          const nextCard = messages[index + 1].card;

          if (nextCard?.kind !== 'toolGroup') break;

          run.push(nextCard);
          index += 1;
        }

        items.push({
          kind: 'mergedToolGroup',
          id: message.id,
          card: mergeToolGroupRun(run),
        });

        continue;
      }

      items.push({ kind: 'message', message });
    }

    return items;
  }, [messages]);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Whether the view is currently "following" the bottom. Starts true so the
  // newest message is visible on open; flipped false the moment the user
  // scrolls up to read, so streamed tokens stop yanking them back down.
  const pinnedRef = useRef(true);

  // How many operator-sent (user) messages we've already auto-scrolled for. A
  // brand-new user send bumps this count and always jumps into view — even if
  // they'd scrolled up — while streamed assistant tokens respect their scroll
  // position. Counting (rather than tracking the tail message id) is immune to
  // in-place id swaps: when `user_message_persisted` replaces the optimistic id
  // with the server id, the count is unchanged, so we never yank the reader
  // back down for what is the same message.
  const userSendCountRef = useRef(0);

  // Surfaced only when the user has scrolled away from the bottom while new
  // content is arriving below — a tap-to-catch-up affordance.
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;

    if (el === null) return;

    el.scrollTop = el.scrollHeight;
    pinnedRef.current = true;
    setShowJumpToLatest(false);
  }, []);

  // Re-derive "are we following the bottom?" whenever the user scrolls. Inside
  // the near-bottom band we keep auto-following; once they scroll up we stop and
  // surface the jump-to-latest button rather than fight their scroll.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;

    if (el === null) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    const nearBottom = distanceFromBottom <= NEAR_BOTTOM_PX;

    pinnedRef.current = nearBottom;
    setShowJumpToLatest(!nearBottom);
  }, []);

  // After content changes, follow the bottom ONLY when pinned — except a brand-
  // new user send always scrolls into view (you want to see your own message).
  // Streamed assistant tokens never override a reader who has scrolled up.
  // useLayoutEffect runs before paint so a legitimately-pinned view never
  // flashes mid-scroll. isThinking is a dep so the indicator below the newest
  // message is followed too.
  useLayoutEffect(() => {
    const el = scrollRef.current;

    if (el === null) return;

    const userSendCount = messages.reduce(
      (count, message) => (message.role === 'user' ? count + 1 : count),
      0,
    );

    // A genuine new send grows the count; an optimistic→server id swap leaves it
    // flat, so a persist event can't masquerade as a fresh send and scroll the
    // reader away from the history they're reading.
    const newUserSend = userSendCount > userSendCountRef.current;

    userSendCountRef.current = userSendCount;

    if (pinnedRef.current || newUserSend) {
      el.scrollTop = el.scrollHeight;
      pinnedRef.current = true;
      setShowJumpToLatest(false);
    } else {
      // Content grew below the fold while the user reads above — offer catch-up.
      setShowJumpToLatest(true);
    }
  }, [messages, isThinking]);

  return (
    <div
      className={cx(
        'tw-relative',
        'tw-flex',
        'tw-flex-col',
        'tw-min-h-0',
        'tw-flex-1',
      )}
    >
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid="chat-message-scroll"
        className={cx(
          'tw-flex',
          'tw-flex-col',
          'tw-flex-1',
          'tw-min-h-0',
          'tw-overflow-y-auto',
          'tw-px-16',
          'tw-py-12',
          'tw-gap-12',
          className,
        )}
      >
        {/* Stable live region — announces at most twice per turn (start/end)
            regardless of how many tokens land. AssistantMessage's per-bubble
            cursor stays aria-hidden so it doesn't double-announce. */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="tw-sr-only"
        >
          {isAnyAssistantStreaming || isThinking ? 'Assistant is typing' : ''}
        </div>
        {transcriptItems.map((item, itemIndex) => {
          if (item.kind === 'mergedToolGroup') {
            return (
              <ChatCardSlot
                key={item.id}
                card={item.card}
                onConfirm={onCardConfirm}
                onNavigate={onCardNavigate}
              />
            );
          }

          const { message } = item;

          if (message.card) {
            return (
              <ChatCardSlot
                key={message.id}
                card={message.card}
                onConfirm={onCardConfirm}
                onNavigate={onCardNavigate}
              />
            );
          }

          if (message.role === 'user') {
            return (
              <UserMessage
                key={message.id}
                message={message}
                onRetry={onRetry}
              />
            );
          }

          return (
            <AssistantMessage
              key={message.id}
              message={message}
              feedbackSlot={
                renderFeedback ? renderFeedback(message, itemIndex) : undefined
              }
            />
          );
        })}
        {isThinking && <ThinkingIndicator />}
      </div>

      {showJumpToLatest && (
        <button
          type="button"
          data-testid="chat-jump-to-latest-button"
          onClick={scrollToBottom}
          aria-label="Jump to latest"
          className={cx(
            'tw-absolute',
            'tw-left-1/2',
            'tw--translate-x-1/2',
            'tw-bottom-12',
            'tw-flex',
            'tw-items-center',
            'tw-gap-6',
            'tw-rounded-full',
            'tw-border-none',
            // Design-system brand-orange action surface (same token as a
            // Filled/Orange Button) — sets the orange background + readable
            // label color + matching shadow, so it reads clearly against the
            // chat transcript.
            'tw-surface-orange',
            'tw-px-12',
            'tw-py-6',
            'tw-text-12',
            'tw-cursor-pointer',
            'enabled:hover:tw-brightness-110',
          )}
        >
          <Icon kind="arrowDown" />
          Latest
        </button>
      )}
    </div>
  );
}
